import { useCallback } from "react";
import { generatePath, useNavigate } from "react-router";

import { useCustomMutation } from "@/hooks/use-graphql-client";

import { PATHS } from "@/routes/paths";

import { CampaignList_Top_Query } from "@/components/features/marketing/campaign/top/model/repository/query/query";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { VoucherForCampaignUpdate_EditPage_Mutation } from "./mutation";
import { viewModelToInput } from "../../_shared";

import type { MarketingCampaignFormModel } from "../../../type";

type Props = {
	onResetViewModel: () => void;
};

export const useExistingCampaignPublish = ({ onResetViewModel }: Props) => {
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const { mutationFunc } = useCustomMutation(VoucherForCampaignUpdate_EditPage_Mutation);

	const navigate = useNavigate();
	const onGenerateCampaignTopPath = useCallback(
		(hotelID: string) => generatePath(PATHS.HOTEL.MARKETING.CAMPAIGN.TOP, { hotelID }),
		[],
	);
	const onTransitionToCampaignTop = useCallback(
		(hotelID: string) => {
			navigate(onGenerateCampaignTopPath(hotelID));
		},
		[onGenerateCampaignTopPath, navigate],
	);

	const onPublish = useCallback(
		(viewModel: MarketingCampaignFormModel["ViewModel"]) => {
			if (viewModel.inSale.value) {
				notifyUserEventInfo({
					variant: "error",
					title: "公開されているキャンペーンを非公開にすることはできません",
				});
				return;
			}

			const confirmed = window.confirm(
				"公開するとゲストにメールが送信されます。公開してもよろしいですか？",
			);
			if (!confirmed) return;

			showLoadingScreenCallback();

			const input = viewModelToInput({ viewModel });
			const overrideInput = {
				...input,
				inSale: true,
			};

			mutationFunc({
				variables: {
					input: overrideInput,
				},
				refetchQueries: [
					{
						query: CampaignList_Top_Query,
						variables: { hotelID: overrideInput.hotelID },
					},
				],
			})
				.then(({ data, errors }) => {
					if (!errors && !data?.voucherForCampaignUpdate.userErrors.length) {
						notifyUserEventInfo({
							variant: "default",
							title: "キャンペーンを保存・公開しました",
						});
						onResetViewModel();

						setTimeout(() => {
							onTransitionToCampaignTop(overrideInput.hotelID);
						}, 100);
					} else {
						notifyUserEventInfo({
							variant: "error",
							title: "キャンペーンの保存・公開に失敗しました",
						});
					}
				})
				.finally(() => {
					hideLoadingScreenCallback();
				});
		},
		[
			hideLoadingScreenCallback,
			mutationFunc,
			onResetViewModel,
			onTransitionToCampaignTop,
			showLoadingScreenCallback,
		],
	);

	return { onPublish };
};
